<script lang="ts" setup>
// Tiroir de navigation latéral (port de MobileNavDrawer) — branché sur la nav réelle
// (useSidebarNav : liens ARC externes + routes internes + gates de permission, même source
// que la sidebar desktop v2) et l'utilisateur courant. Items à groupes (Commerce, Admin) =
// accordéons ; pied (compte, thème, déconnexion).
import { storeToRefs } from 'pinia';
import { type NavItem, navTag, useSidebarNav } from '~/composables/useSidebarNav';
import { isBrowserHandledClick } from '~/utils/popupNavigation';

withDefaults(defineProps<{ theme?: 'light' | 'dark'; active?: string }>(), { theme: 'light', active: 'Dossiers' });
const emit = defineEmits<{ close: []; toggleTheme: [] }>();

const route = useRoute();
const usersStore = useUsersStore();
const { authUserData } = storeToRefs(usersStore);
const { mainItems, footerItems } = useSidebarNav();

const visibleMainItems = computed<NavItem[]>(() => mainItems.value.filter(i => i.visible));
const visibleFooterItems = computed<NavItem[]>(() => footerItems.value.filter(i => i.visible));

const me = computed(() => ({
  display: authUserData.value?.username ?? '',
  initials: (authUserData.value?.username ?? '').slice(0, 2).toUpperCase() || '-',
  role: authUserData.value?.position?.slug ?? '',
}));

const openGroups = ref(new Set<string>());
function toggleGroup (label: string): void {
  if (openGroups.value.has(label)) { openGroups.value.delete(label); } else { openGroups.value.add(label); }
}

function isActive (item: NavItem): boolean {
  return item.page && !item.external ? route.path.startsWith(item.page) : false;
}
function go (item: NavItem, event: MouseEvent): void {
  if (item.groups) { toggleGroup(item.label); return; }
  if (isBrowserHandledClick(event)) { return; }
  event.preventDefault();
  emit('close');
  if (item.page && !item.disabled) { navigateTo(item.page, { external: Boolean(item.external) }); }
}
function goChild (page: string, event: MouseEvent): void {
  if (isBrowserHandledClick(event)) { return; }
  event.preventDefault();
  emit('close');
  navigateTo(page);
}
</script>

<template>
  <div
    class="gm-drawer-scrim"
    @click="emit('close')"
  >
    <nav
      class="gm-drawer"
      @click.stop
    >
      <div class="gm-drawer-hd">
        <span class="gm-drawer-brand">GoldoRAC</span>
        <button
          class="gm-sheet-x"
          @click="emit('close')"
        >
          <DsIcon
            name="times"
            :size="14"
          />
        </button>
      </div>
      <div class="gm-drawer-body">
        <template
          v-for="it in visibleMainItems"
          :key="it.label"
        >
          <component
            :is="navTag(it)"
            :href="navTag(it) === 'a' ? it.page : undefined"
            :type="navTag(it) === 'button' ? 'button' : undefined"
            :aria-disabled="it.disabled ? true : undefined"
            :aria-expanded="it.groups ? openGroups.has(it.label) : undefined"
            :class="['gm-drawer-item', { on: isActive(it), open: it.groups && openGroups.has(it.label) }]"
            @click="go(it, $event)"
          >
            <span class="gm-drawer-ic"><DsIcon
              :name="it.icon"
              :size="18"
            /></span>
            <span class="gm-drawer-lbl">{{ it.label }}</span>
            <span
              v-if="it.groups"
              :class="['gm-drawer-caret', { up: openGroups.has(it.label) }]"
            />
          </component>
          <div
            v-if="it.groups && openGroups.has(it.label)"
            class="gm-drawer-sub"
          >
            <div
              v-for="g in it.groups"
              :key="g.title"
              class="gm-drawer-grp"
            >
              <div class="gm-drawer-grp-t">
                {{ g.title }}
              </div>
              <a
                v-for="c in g.items"
                :key="c.page"
                :href="c.page"
                class="gm-drawer-link"
                @click="goChild(c.page, $event)"
              >
                <span class="gm-drawer-dot" />{{ c.label }}
              </a>
            </div>
          </div>
        </template>

        <template
          v-for="it in visibleFooterItems"
          :key="it.label"
        >
          <div class="gm-drawer-sep" />
          <component
            :is="navTag(it)"
            :href="navTag(it) === 'a' ? it.page : undefined"
            :type="navTag(it) === 'button' ? 'button' : undefined"
            :aria-disabled="it.disabled ? true : undefined"
            :aria-expanded="it.groups ? openGroups.has(it.label) : undefined"
            :class="['gm-drawer-item', { open: openGroups.has(it.label) }]"
            @click="go(it, $event)"
          >
            <span class="gm-drawer-ic"><DsIcon
              :name="it.icon"
              :size="18"
            /></span>
            <span class="gm-drawer-lbl">{{ it.label }}</span>
            <span
              v-if="it.groups"
              :class="['gm-drawer-caret', { up: openGroups.has(it.label) }]"
            />
          </component>
          <div
            v-if="it.groups && openGroups.has(it.label)"
            class="gm-drawer-sub"
          >
            <div
              v-for="g in it.groups"
              :key="g.title"
              class="gm-drawer-grp"
            >
              <div class="gm-drawer-grp-t">
                {{ g.title }}
              </div>
              <a
                v-for="c in g.items"
                :key="c.page"
                :href="c.page"
                class="gm-drawer-link"
                @click="goChild(c.page, $event)"
              >
                <span class="gm-drawer-dot" />{{ c.label }}
              </a>
            </div>
          </div>
        </template>
      </div>
      <div class="gm-drawer-ft">
        <div class="gm-drawer-me">
          <span class="gm-drawer-av">{{ me.initials }}</span>
          <span class="gm-drawer-me-info">
            <span class="gm-drawer-me-name">{{ me.display }}</span>
            <span
              v-if="me.role"
              class="gm-drawer-me-role"
            >{{ me.role }}</span>
          </span>
        </div>
        <button
          class="gm-drawer-item"
          @click="emit('toggleTheme')"
        >
          <span class="gm-drawer-ic"><DsIcon
            :name="theme === 'dark' ? 'sun' : 'moon'"
            :size="17"
          /></span>
          <span class="gm-drawer-lbl">{{ theme === 'dark' ? 'Thème clair' : 'Thème sombre' }}</span>
          <span :class="['gm-switch', { on: theme === 'dark' }]" />
        </button>
      </div>
    </nav>
  </div>
</template>
